import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLang } from './lang';
import { findHighlightRanges } from './highlight';
import { pickDistractors, clozeDistractors } from './quizUtils';
import { cefrOf } from './levels';
import { FREQUENCY_VALUES } from './levels';
import Header from './Header';
import Footer from './Footer';
import { jsonAuthHeaders } from './apiAuth';

// ── Test de niveau (page à part, sans compte) ────────────────────────────────
// Estimation SÉRIEUSE et rapide du vocabulaire de fréquence : test adaptatif
// bayésien (voir le pavé sur pCorrect). Chaque question se pose au niveau estimé,
// chaque réponse déplace l'estimation, et le hasard est modélisé pour ne pas être
// pris pour du savoir. Anti-triche : chronomètre par question, formats variés
// (RU→FR, cursive, FR→RU, texte à trou, audio), aucune correction affichée.

const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";

// Plus de bandes de rang figées : on tire les mots dans une FENÊTRE calée sur
// l'estimation courante (voir WINDOW). Les bandes faisaient jusqu'à un rapport 4
// entre leur premier et leur dernier mot — poser « au niveau estimé » n'y voulait
// pas dire grand-chose.
const START_RANK = 700;    // point de départ neutre (autour de A2/B1)
const WINDOW = 1.6;        // fenêtre multiplicative autour de l'estimation : ÷1,6 … ×1,6
const MAX_RANK = 6000;
const MAX_Q = 22;          // plafond de questions (≈ 4 min 30)
const MIN_Q = 12;          // plancher : en dessous, l'estimation est trop bruitée
const QUESTION_SECONDS = 12;
const OPTIONS = 6;

// ── Estimation du niveau ─────────────────────────────────────────────────────
// L'ancien moteur était un escalier : 3 questions par bande, ≥ 2 bonnes = on monte.
// Deux défauts de fond, qui le faisaient se tromper.
//
//  1. LE HASARD PASSAIT. Avec 4 options, répondre au pif donne 25 % par question,
//     donc P(≥ 2 sur 3) = 15,6 % : une bande sur six était « validée » par pure
//     chance, et l'erreur se propageait vers le haut.
//  2. LE SCORE BRUT SERVAIT DE MESURE. `correct/asked` était utilisé tel quel, alors
//     qu'un candidat qui ne sait RIEN score déjà 25 % : un quart de chaque bande
//     était offert.
//
// Ici : estimation bayésienne classique du testing adaptatif. On pose θ = le rang
// jusqu'auquel la personne connaît les mots, et on tient une distribution de
// probabilité sur θ, mise à jour à CHAQUE réponse (aucune n'est jetée, contrairement
// aux blocs). La probabilité de répondre juste à un mot de rang r vaut
//
//     P(juste | θ) = g + (1 − g) · σ( a · (ln θ − ln r) )
//
// où g est le plancher de hasard — il est DANS le modèle, donc corrigé par
// construction — et `a` la pente (la netteté de la frontière entre ce qu'on sait et
// ce qu'on ignore).
//
// g N'EST PAS 1/OPTIONS. C'est ce qui faisait encore surestimer le niveau, et de
// beaucoup. 1/6 serait le taux de réussite de quelqu'un qui coche VRAIMENT au
// hasard ; personne ne joue comme ça. Devant six propositions, on élimine. Les
// leurres viennent du même niveau de fréquence que le mot testé, donc un candidat
// arrivé à sa frontière en connaît souvent deux ou trois : il ne tire plus à un
// contre six mais à un contre trois. Sur les formats à propositions russes
// (fr_ru, cloze, audio_ru) c'est encore plus marqué — la longueur du mot, la
// terminaison, la syntaxe de la phrase à trou éliminent des candidats sans rien
// savoir du sens.
//
// Or dans ce modèle, une bonne réponse sur un mot qu'on ignore est lue comme une
// preuve de savoir d'autant plus forte que g est bas. Sous-estimer le hasard, c'est
// donc payer chaque coup de chance en niveau — et les coups de chance s'accumulent
// vers le haut, parce qu'une bonne réponse fait monter la question suivante.
// Valeur retenue : 0,30, mesurée par simulation comme le meilleur compromis (voir
// plus bas). Assumé : le test SOUS-estime un peu qui ne devine jamais. C'est le bon
// sens de l'erreur pour un test de niveau — on annonce un niveau qu'on tient.
const GUESS = 0.30;
const SLOPE = 1.7;

// Mesuré, pas supposé (simulation : 800 candidats par niveau, répondant selon une
// AUTRE loi que le modèle — sinon on ne fait que se donner raison — et devinant à
// 30-42 % sur ce qu'ils ignorent, comme on devine devant six propositions issues
// du même niveau de fréquence).
//
//   vrai niveau        annoncé AVANT        annoncé APRÈS
//   800 mots  (A2)     1 100  (A2)            780  (A2)
//   2 000     (B1)     2 760  (B2)          1 960  (B1)
//   3 500     (B2)     4 610  (C1)          3 470  (B2)
//   5 200     (C1)     5 170  (C1)          4 880  (C1)
//
// « C1 » annoncé à un vrai B2 : 65 % → 33 %. À un vrai B1 : 18 % → 4 %. Erreur
// (log-RMS) 0,54 → 0,34. Le C1 restant sur un B2 n'est pas une erreur du moteur :
// 3 500 et le seuil C1 (4 000) sont voisins, et l'intervalle affiché le dit.

// Grille log-espacée sur les rangs : c'est en ordre de grandeur que le vocabulaire
// se raisonne (300 → 600 est un pas aussi grand que 3000 → 6000).
const GRID = Array.from({ length: 90 }, (_, i) => Math.exp(Math.log(40) + (i / 89) * (Math.log(6500) - Math.log(40))));

// Tous les formats ne se valent pas : reconnaître un mot écrit est plus facile que
// le retrouver à l'oral ou le produire depuis sa traduction. Un format difficile est
// donc traité comme si le mot était PLUS RARE qu'il ne l'est (facteur > 1), sinon la
// difficulté du format se confondait avec la rareté du mot et brouillait la mesure.
// Valeurs de jugement, pas de calibration empirique — à revoir avec des données.
const FORMAT_FACTOR = {
  ru_fr:    0.8,   // voir le mot russe → choisir le sens : le plus facile
  ru_fr_cur: 1.5,  // le même, mais en CURSIVE manuscrite (voir plus bas)
  audio_ru: 1.15,  // entendre → reconnaître l'orthographe
  fr_ru:    1.25,  // partir du sens → retrouver le mot russe
  audio_fr: 1.3,   // entendre → comprendre, sans l'écrit
  cloze:    1.4,   // compléter une phrase : demande aussi la syntaxe
};

const pCorrect = (theta, rank, factor) => {
  const d = Math.max(1, rank * factor);
  return GUESS + (1 - GUESS) / (1 + Math.exp(-SLOPE * (Math.log(theta) - Math.log(d))));
};

// Quantile d'une distribution discrète (poids alignés sur GRID).
const quantile = (post, p) => {
  const total = post.reduce((s, x) => s + x, 0);
  let acc = 0;
  for (let i = 0; i < GRID.length; i++) {
    acc += post[i] / total;
    if (acc >= p) return GRID[i];
  }
  return GRID[GRID.length - 1];
};

const shuffle = (a) => a.slice().sort(() => Math.random() - 0.5);
const audioUrl = (id, type) => `/static/audio/${id}_${type}.mp3`;

// Un vivier de mots dans la fenêtre [lo, hi]. Le serveur trie en ORDER BY RANDOM()
// et plafonne à 12 lignes : redemander la MÊME fenêtre rend d'autres mots, ce dont
// on se sert quand le vivier est épuisé (voir nextQuestion).
async function fetchWindow(lo, hi, apiLang) {
  try {
    const res = await fetch('/api/quiz_batch', {
      method: 'POST',
      headers: jsonAuthHeaders(),
      body: JSON.stringify({ lang: apiLang, limit: 12, buckets: [{ rank_min: Math.round(lo) - 1, rank_max: Math.round(hi) }] }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// Coquille avec le MÊME header et footer que le reste du site.
const Shell = ({ lang, cursive, toggleCursive, children }) => (
  <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
    <div className="max-w-xl md:max-w-2xl mx-auto px-4 py-6 min-h-screen flex flex-col">
      <Header lang={lang} cursive={cursive} toggleCursive={toggleCursive} />
      <div className="flex flex-col gap-6 mt-6 flex-1">{children}</div>
      <Footer lang={lang} />
    </div>
  </div>
);

// Chrono de la question : un disque qui se vide. Deux formes, zéro chiffre — la
// barre dit où on en est dans le test, le disque dit combien de temps il reste
// sur CETTE question. On lit le temps sans le lire.
// L'astuce : un cercle de rayon r avec un trait de 2r part du centre et remplit
// tout le disque ; `stroke-dasharray` le découpe donc en camembert.
const RING_R = 9;
const RING_C = 2 * Math.PI * RING_R;
const TimerDisc = ({ left, total, title }) => {
  const frac = Math.max(0, Math.min(1, left / total));
  const low = left <= 3;
  return (
    <svg viewBox="0 0 36 36" className="shrink-0 w-7 h-7 -rotate-90" role="img" aria-label={title}>
      <title>{title}</title>
      <circle cx="18" cy="18" r="17" fill="none" stroke="#1e293b" strokeWidth="2" />
      <circle
        cx="18" cy="18" r={RING_R} fill="none"
        stroke={low ? '#f87171' : '#60a5fa'}
        strokeWidth={RING_R * 2}
        strokeDasharray={`${frac * RING_C} ${RING_C}`}
        style={{ transition: 'stroke-dasharray 1s linear' }}
      />
    </svg>
  );
};

// Réglage du son : il vivait sur l'écran d'intro, supprimé. Sans lui, l'autoplay
// des questions audio n'était plus activable de cette page (il fallait aller dans
// le quiz pour basculer la préférence, qui est partagée via localStorage).
const SoundToggle = ({ enabled, onToggle, lang }) => (
  <button
    onClick={onToggle}
    title={enabled ? lang.ui.soundOn : lang.ui.soundOff}
    aria-label={enabled ? lang.ui.soundOn : lang.ui.soundOff}
    className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
      enabled ? 'bg-blue-600 hover:bg-blue-500' : 'bg-slate-800 hover:bg-slate-700'
    }`}
  >
    <svg viewBox="0 0 24 24" className={`w-4 h-4 fill-white ${enabled ? '' : 'opacity-50'}`}>
      {enabled
        ? <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
        : <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />}
    </svg>
  </button>
);

const Speaker = ({ onClick, title, big }) => (
  <button
    onClick={onClick}
    title={title}
    className={`shrink-0 inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-500 transition-colors ${big ? 'w-16 h-16' : 'w-9 h-9'}`}
  >
    <svg viewBox="0 0 24 24" className={`fill-white ${big ? 'w-7 h-7' : 'w-4 h-4'}`}>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
    </svg>
  </button>
);

export default function LevelTest({ play, playAuto, soundEnabled, toggleSound, cursive, toggleCursive, userId }) {
  const lang = useLang();
  const t = lang.ui.levelTest;
  const wordField = lang.wordField;
  const exRuField = lang.exampleRuField;

  // Plus d'écran d'intro : les explications vivent sur la page statique
  // (/mot/<lang>/<test>.html), qui est le point d'entrée du test et la seule
  // version indexable. Arriver ici, c'est avoir déjà cliqué « Commencer » : on
  // enchaîne donc directement sur le chargement puis la 1re question.
  const [phase, setPhase] = useState('loading'); // loading | question | result | error
  const [q, setQ] = useState(null);
  const [qNum, setQNum] = useState(0);
  const [timeLeft, setTimeLeft] = useState(QUESTION_SECONDS);
  const [result, setResult] = useState(null);
  const [locked, setLocked] = useState(false);
  const [copied, setCopied] = useState(false);

  // Vivier courant + la fenêtre de rangs dans laquelle il a été tiré : on le
  // renouvelle quand l'estimation a assez bougé, ou quand il est épuisé.
  const poolRef    = useRef({ words: [], lo: 0, hi: 0 });
  const usedRef    = useRef(new Set());         // ids déjà posés
  const askedRef   = useRef(0);
  // Distribution de probabilité sur θ (le rang jusqu'où la personne connaît les
  // mots), alignée sur GRID. Prior uniforme : on ne présume rien du visiteur.
  const postRef    = useRef(GRID.map(() => 1 / GRID.length));
  // Question en cours (type + rang du mot) : sert à la mise à jour à la réponse.
  const askedQRef  = useRef(null);
  const timerRef   = useRef(null);
  const soundRef   = useRef(soundEnabled);
  useEffect(() => { soundRef.current = soundEnabled; }, [soundEnabled]);
  // `finish` doit rester STABLE (il est en dépendance des effets de boucle) : on
  // passe donc l'identifiant et la langue par des refs plutôt qu'en dépendances.
  const userIdRef  = useRef(userId);
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  const langRef    = useRef(lang.apiLang);
  useEffect(() => { langRef.current = lang.apiLang; }, [lang.apiLang]);

  const clearTimer = () => clearInterval(timerRef.current);
  useEffect(() => () => clearTimer(), []);

  // Construit une question sur `word`, avec `pool` comme réserve de leurres. Le
  // choix du mot appartient à nextQuestion : il se fait sur le RANG, et c'est tout
  // l'intérêt d'un test adaptatif. Avant, cette fonction tirait le mot au hasard
  // dans le vivier (`shuffle(fresh)[0]`) juste après que nextQuestion l'ait trié
  // par proximité au niveau estimé — le tri était donc jeté, et la moitié des
  // questions tombait loin de la frontière, là où une réponse n'apprend presque
  // rien. Elle pouvait en plus REPOSER un mot déjà vu quand le vivier était vide :
  // le moteur comptait alors comme une preuve neuve une réponse que le candidat
  // venait de donner, ce qui gonflait la confiance sur du vide.
  const buildQuestion = useCallback((word, pool) => {
    const playable = pool.filter((w) => w.russe && w[wordField]);
    if (playable.length < 4) return null;

    // Leurres filtrés PAR LE SENS, comme dans le quiz. La dédup se faisait sur la
    // chaîne entière : « Boire » et « boire, prendre un verre » passaient pour
    // distincts, donc la question avait deux bonnes réponses et l'une des deux était
    // comptée fausse. Sur un TEST, une telle question ne fait pas que gêner : elle
    // fausse la mesure du niveau, et toujours vers le bas.
    const distract = (n) => pickDistractors(word, pool, wordField, n);

    const transQuestion = () => {
      const d = distract(OPTIONS - 1);
      if (d.length < 3) return null;
      return shuffle([word, ...d]).map((w) => ({ k: w.id, label: w[wordField], correct: w.id === word.id }));
    };

    // `ru_fr_cur` : le mot est affiché en CURSIVE MANUSCRITE. En russe, l'écriture
    // à la main ne ressemble pas à l'imprimé (д, т, и changent complètement de
    // forme) : savoir lire l'un ne garantit pas de savoir lire l'autre, et c'est ce
    // qui bloque les débutants devant une note écrite à la main. C'est donc une
    // compétence à part entière, qu'aucun autre format ne mesurait.
    const types = ['ru_fr', 'ru_fr_cur', 'fr_ru'];
    if (word[exRuField] && findHighlightRanges(word[exRuField], word.russe).length) types.push('cloze');
    if (soundRef.current) types.push('audio_fr', 'audio_ru');
    const type = types[Math.floor(Math.random() * types.length)];

    if (type === 'ru_fr' || type === 'ru_fr_cur' || type === 'audio_fr') {
      const options = transQuestion();
      if (!options) return null;
      return { type, word, promptRu: type === 'audio_fr' ? null : word.russe, options };
    }

    // fr_ru / cloze / audio_ru : on choisit le MOT RUSSE parmi des leurres russes.
    // Pour le texte à trou, les leurres doivent en plus venir d'un autre champ
    // lexical : sinon plusieurs mots complètent la phrase aussi bien.
    const dr = (type === 'cloze'
      ? clozeDistractors(word, pool, wordField, OPTIONS - 1)
      : distract(OPTIONS - 1)).filter((w) => w.russe);
    if (dr.length < 3) {
      const options = transQuestion();
      return options ? { type: 'ru_fr', word, promptRu: word.russe, options } : null;
    }
    const options = shuffle([word, ...dr]).map((w) => ({ k: w.id, label: w.russe, correct: w.id === word.id, ru: true }));
    if (type === 'cloze') {
      const [s, e] = findHighlightRanges(word[exRuField], word.russe)[0];
      return { type, word, options, cloze: { before: word[exRuField].slice(0, s), after: word[exRuField].slice(e) } };
    }
    // audio_ru : on entend le mot, on reconnaît son ORTHOGRAPHE. Modalité absente
    // jusqu'ici — savoir qu'un son correspond à des lettres est une compétence à
    // part, et elle discrimine bien chez ceux qui n'apprennent que par l'écrit.
    if (type === 'audio_ru') return { type, word, options };
    return { type: 'fr_ru', word, options, prompt: word[wordField] };
  }, [wordField, exRuField]);

  // Fin du test : la distribution courante EST le résultat. On rend la médiane, et
  // l'intervalle à 80 % — parce qu'annoncer « 2 000 mots » au mot près serait une
  // fausse précision : 18 questions ne mesurent pas mieux que ça.
  const finish = useCallback(() => {
    clearTimer();
    const post = postRef.current;
    const med = quantile(post, 0.5);
    const lo = quantile(post, 0.1);
    const hi = quantile(post, 0.9);
    const round = (x) => (x < 1000 ? Math.round(x / 50) * 50 : Math.round(x / 100) * 100);
    const res = {
      words: Math.max(0, Math.min(6000, round(med))),
      low: Math.max(0, round(lo)),
      high: Math.min(6000, round(hi)),
      cefr: cefrOf(med),
      asked: askedRef.current,
    };
    setResult(res);
    setPhase('result');

    // Mémorisation du passage, avec ou sans compte : l'identifiant anonyme suffit
    // (c'est le même pivot qu'un compte, qui ne fait que résoudre vers lui). Envoi
    // en aveugle — on n'attend pas la réponse et on n'affiche jamais d'erreur : le
    // résultat appartient à l'utilisateur, il n'a pas à dépendre du réseau.
    if (userIdRef.current) {
      fetch('/api/level_test', {
        method: 'POST',
        headers: jsonAuthHeaders(),
        body: JSON.stringify({ user_id: userIdRef.current, lang: langRef.current, ...res }),
      }).catch(() => {});
    }
  }, []);

  const nextQuestion = useCallback(async () => {
    // La question suivante se pose LÀ OÙ ELLE APPREND LE PLUS : à la médiane
    // courante, c'est-à-dire à la frontière présumée entre ce que la personne sait et
    // ce qu'elle ignore. Une question trop facile ou trop dure n'apporte presque rien.
    const med = askedRef.current === 0 ? START_RANK : quantile(postRef.current, 0.5);
    const lo = Math.max(1, med / WINDOW);
    const hi = Math.min(MAX_RANK, med * WINDOW);

    // On renouvelle le vivier quand la fenêtre a dérivé (l'estimation a bougé), et
    // aussi quand il ne reste plus de mot neuf : le serveur tire au hasard, donc
    // redemander la même fenêtre rend d'autres mots. Reposer un mot déjà vu ferait
    // compter deux fois la même information.
    const pool = poolRef.current;
    const derive = !pool.words.length
      || lo / Math.max(1, pool.lo) > 1.5 || Math.max(1, pool.hi) / hi > 1.5;
    const epuise = () => !poolRef.current.words.some((w) => !usedRef.current.has(w.id));
    if (derive || epuise()) {
      poolRef.current = { words: await fetchWindow(lo, hi, lang.apiLang), lo, hi };
    }
    // Le vivier peut rester sec (fenêtre pauvre en mots exploitables) : on élargit
    // une fois avant d'abandonner, plutôt que de conclure sur trop peu de réponses.
    if (epuise()) {
      const l2 = Math.max(1, med / (WINDOW * WINDOW));
      const h2 = Math.min(MAX_RANK, med * WINDOW * WINDOW);
      poolRef.current = { words: await fetchWindow(l2, h2, lang.apiLang), lo: l2, hi: h2 };
    }

    const vivier = poolRef.current.words;
    const fresh = vivier.filter((w) => !usedRef.current.has(w.id));
    // Parmi les mots neufs, celui dont le rang est le PLUS PROCHE de la médiane :
    // c'est là que la réponse tranche le plus.
    const cible = fresh.length && fresh.some((w) => w.rank)
      ? fresh.slice().sort((a, b) =>
          Math.abs(Math.log(a.rank || 1) - Math.log(med)) - Math.abs(Math.log(b.rank || 1) - Math.log(med)))[0]
      : shuffle(fresh)[0];

    const question = cible ? buildQuestion(cible, vivier) : null;
    if (!question) {
      if (askedRef.current === 0) setPhase('error');
      else finish();
      return;
    }
    usedRef.current.add(question.word.id);
    askedRef.current += 1;
    // Rang réel du mot posé : c'est LUI qui entre dans le modèle. À défaut (l'API ne
    // renvoie pas le rang — backend pas redémarré), on prend le centre de la fenêtre.
    askedQRef.current = { type: question.type, rank: question.word.rank ?? med };
    setQ(question);
    setQNum(askedRef.current);
    setLocked(false);
    setTimeLeft(QUESTION_SECONDS);
    setPhase('question');
    if (question.type === 'audio_fr') playAuto(audioUrl(question.word.id, 'word'));
  }, [lang.apiLang, buildQuestion, finish, playAuto]);

  const answer = useCallback((correct) => {
    if (locked) return;
    setLocked(true);
    clearTimer();

    // Mise à jour bayésienne : CHAQUE réponse compte, y compris celles qui, dans
    // l'ancien moteur, tombaient dans un bloc jamais évalué (fin de test).
    const asked = askedRef.current;
    const cur = askedQRef.current;
    if (cur) {
      const factor = FORMAT_FACTOR[cur.type] ?? 1;
      const rank = cur.rank;
      postRef.current = postRef.current.map((w, i) => {
        const p = pCorrect(GRID[i], rank, factor);
        return w * (correct ? p : 1 - p);
      });
      const s = postRef.current.reduce((a, b) => a + b, 0) || 1;
      postRef.current = postRef.current.map((w) => w / s);
    }

    // Arrêt : soit l'intervalle est assez serré pour qu'une question de plus ne
    // change plus la réponse, soit on atteint le plafond. Le plancher MIN_Q évite
    // de conclure sur une poignée de réponses chanceuses.
    const hi = quantile(postRef.current, 0.9);
    const lo = quantile(postRef.current, 0.1);
    const assezPrecis = asked >= MIN_Q && hi / Math.max(1, lo) < 1.9;

    setTimeout(() => {
      if (asked >= MAX_Q || assezPrecis) finish();
      else nextQuestion();
    }, 160); // aucune correction montrée (anti-triche) : court battement
  }, [locked, finish, nextQuestion]);

  // Chronomètre par question (temps écoulé = réponse fausse).
  useEffect(() => {
    if (phase !== 'question') return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { clearTimer(); answer(false); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearTimer();
  }, [phase, qNum]); // eslint-disable-line react-hooks/exhaustive-deps

  // Démarrage automatique. La ref, et pas un drapeau d'état, parce que StrictMode
  // invoque les effets deux fois au montage : sans elle, deux questions étaient
  // tirées et la première était perdue (le compteur partait à 2).
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    nextQuestion();
  }, [nextQuestion]);

  // Remise à zéro complète puis relance immédiate. Sert au « Refaire le test » du
  // résultat comme au « Réessayer » de l'erreur : il n'y a plus d'écran d'intro sur
  // lequel retomber, un test qui repart repart pour de bon.
  const retake = useCallback(() => {
    poolRef.current = { words: [], lo: 0, hi: 0 };
    usedRef.current = new Set();
    askedRef.current = 0;
    postRef.current = GRID.map(() => 1 / GRID.length);
    askedQRef.current = null;
    setResult(null);
    setQ(null);
    setQNum(0);
    setPhase('loading');
    nextQuestion();
  }, [nextQuestion]);

  const train = () => {
    const words = result?.words ?? 0;
    const band = FREQUENCY_VALUES.find((v) => v >= words) ?? FREQUENCY_VALUES[FREQUENCY_VALUES.length - 1];
    try { localStorage.setItem('motrusse_frequency', String(band)); } catch {}
    window.location.assign('/');
  };

  const share = () => {
    if (!result) return;
    // On partage le RÉSULTAT (le texte contient le niveau + le nombre de mots),
    // avec un lien vers la PAGE STATIQUE dédiée (indexable, balises OG propres) —
    // pas le /?test de la SPA. Partage natif sur mobile, sinon copie texte + lien.
    const url = window.location.origin + t.pagePath;
    const words = result.words.toLocaleString(lang.apiLang);
    const text = t.shareText(result.cefr, words);
    if (navigator.share) { navigator.share({ title: t.introTitle, text, url }).catch(() => {}); return; }
    navigator.clipboard?.writeText(`${text} ${url}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => {});
  };

  // ── Rendu ──────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <Shell lang={lang} cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-10">
          <p className="text-slate-400">{lang.ui.errorPrefix} {t.loadError}</p>
          <button onClick={retake} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
            {lang.ui.retry}
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'loading') {
    return (
      <Shell lang={lang} cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24 text-slate-400">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'result' && result) {
    const pct = Math.min(100, (result.words / 6000) * 100);
    return (
      <Shell lang={lang} cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex flex-col items-center gap-6 text-center">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t.resultTitle}</span>
          <div className="flex flex-col items-center gap-1">
            <span className="text-7xl font-extrabold text-blue-300 leading-none">{result.cefr}</span>
            <span className="text-sm text-slate-400">{t.cefrName[result.cefr]}</span>
          </div>
          <div className="w-full max-w-md flex flex-col gap-2">
            <div className="flex items-baseline justify-center gap-1.5">
              <span className="text-2xl font-bold text-white tabular-nums">≈ {result.words.toLocaleString(lang.apiLang)}</span>
              <span className="text-sm text-slate-400">{t.wordsScale}</span>
            </div>
            {/* L'intervalle, affiché SANS DÉTOUR. Une vingtaine de questions ne
                mesure pas un vocabulaire au mot près : annoncer « 2 000 » tout court
                serait une fausse précision, et c'est cette fausse précision qui rend
                un test « faux ». Dire entre quelles bornes on situe la personne,
                c'est la seule façon de ne pas se tromper. */}
            <p className="text-xs text-slate-500 tabular-nums">
              {t.range(result.low.toLocaleString(lang.apiLang), result.high.toLocaleString(lang.apiLang))}
            </p>
            <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-slate-600 tabular-nums px-0.5">
              <span>0</span><span>1500</span><span>3000</span><span>4500</span><span>6000</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 max-w-sm leading-relaxed">{t.disclaimer}</p>
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
            <button onClick={train} className="flex-1 px-5 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-semibold text-white transition-colors">
              {t.ctaTrain}
            </button>
            <button onClick={share} className="flex-1 px-5 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl font-medium text-slate-200 transition-colors">
              {copied ? t.shared : t.share}
            </button>
          </div>
          <button onClick={retake} className="text-xs text-slate-600 hover:text-slate-400 underline underline-offset-4">
            {t.ctaRetake}
          </button>
        </div>
      </Shell>
    );
  }

  // phase === 'question'
  const prompt =
    q?.type === 'fr_ru' ? t.chooseWord :
    q?.type === 'cloze' ? t.fillBlank :
    q?.type === 'audio_fr' ? t.listenPick :
    q?.type === 'audio_ru' ? t.listenSpell :
    q?.type === 'ru_fr_cur' ? t.readCursive :
    t.chooseTranslation;

  return (
    <Shell lang={lang} cursive={cursive} toggleCursive={toggleCursive}>
      <div className="flex items-center gap-2 sm:gap-3">
        <SoundToggle enabled={soundEnabled} onToggle={toggleSound} lang={lang} />
        {/* Avancement DANS LE TEST. Remplace le « ≤ 3:24 » : le test est borné à
            18 questions de 12 s, donc la barre dit déjà le temps qui reste, en
            mieux (elle avance à chaque réponse, même quand on répond vite). */}
        <div
          className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"
          role="progressbar" aria-label={t.progressLbl}
          aria-valuenow={qNum} aria-valuemin={0} aria-valuemax={MAX_Q}
        >
          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, (qNum / MAX_Q) * 100)}%` }} />
        </div>
        <TimerDisc left={timeLeft} total={QUESTION_SECONDS} title={t.timeCap} />
      </div>

      <div className="bg-slate-800 border border-slate-700/50 rounded-2xl px-5 md:px-7 py-8 flex flex-col gap-7 select-none">
        <p className="text-center text-xs uppercase tracking-widest text-slate-500">{prompt}</p>

        <div className="text-center min-h-[3.5rem] flex items-center justify-center">
          {q?.type === 'audio_fr' || q?.type === 'audio_ru' ? (
            <Speaker big onClick={() => play(audioUrl(q.word.id, 'word'))} title={lang.ui.listenAgain} />
          ) : q?.type === 'cloze' ? (
            <p lang="ru" className="text-lg md:text-xl text-blue-100 leading-relaxed" style={{ fontFamily: SERIF }}>
              {q.cloze.before}
              <span className="text-blue-300 font-semibold tracking-widest">·····</span>
              {q.cloze.after}
            </p>
          ) : q?.type === 'fr_ru' ? (
            <p className="text-2xl md:text-3xl font-semibold text-white break-words">{q.prompt}</p>
          ) : (
            <div className="flex items-center justify-center gap-3">
              {/* Cursive = serif en italique, exactement ce que produit le bouton Аа
                  du site. On la pose en style INLINE et non via la classe Tailwind
                  `italic` : la règle de la charte interdit cette classe sur du texte
                  lang="ru" (elle forcerait la cursive partout, y compris en mode
                  imprimerie). Ici elle est voulue, et seulement pour cette question. */}
              <p
                lang="ru"
                className="text-4xl md:text-5xl font-bold text-blue-300 break-all"
                style={{ fontFamily: SERIF, fontStyle: q?.type === 'ru_fr_cur' ? 'italic' : 'normal' }}
              >
                {q?.promptRu}
              </p>
              {/* Pas de haut-parleur sur la question cursive : entendre le mot
                  donnerait la réponse sans avoir eu à déchiffrer l'écriture. */}
              {q?.type !== 'ru_fr_cur' && (
                <Speaker onClick={() => play(audioUrl(q.word.id, 'word'))} title={lang.ui.listen} />
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {q?.options.map((opt) => (
            <button
              key={opt.k}
              lang={opt.ru ? 'ru' : undefined}
              disabled={locked}
              onClick={() => answer(opt.correct)}
              className="px-4 py-4 rounded-xl text-sm md:text-base font-medium border bg-slate-700 text-white border-slate-600 hover:bg-blue-600 hover:border-blue-500 active:scale-95 transition-all break-words disabled:opacity-60 disabled:active:scale-100"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </Shell>
  );
}
